import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import AIConsentModal from "@/components/AIConsentModal";
import { api, formatApiError } from "@/lib/api";
import {
  Star, Trash, Tag, GraduationCap, CurrencyGbp, Plus, X, FloppyDisk, Sparkle,
  CaretDown, ChatTeardropDots, Heart, Sliders, Buildings, ArrowsClockwise,
} from "@phosphor-icons/react";

/**
 * Compare — unified job comparison.
 *
 * One document per job (company + role) holds AI-extracted ad signals, skills,
 * years/level required, salary band, and notes. User sets "what matters most"
 * weights once; we compute a personal fit score per job. We also show how
 * other JobVault users have rated the same employer.
 */

// Dimensions exposed both in the user's "what matters" weights and in the
// AI's ad analysis. `prefKey` lives on /culture/preferences, `adKey` lives
// inside ad_analysis.dimensions.
const DIMENSIONS = [
  { adKey: "hours_culture",      prefKey: "hours_culture",       label: "Work-life balance" },
  { adKey: "formality",          prefKey: "formality",           label: "Formality" },
  { adKey: "structure",          prefKey: "structure",           label: "Structure" },
  { adKey: "growth",             prefKey: "growth_focus",        label: "Growth" },
  { adKey: "salary_transparency",prefKey: "salary_transparency", label: "Salary transparency" },
  { adKey: "diversity",          prefKey: "diversity_focus",     label: "Diversity" },
  { adKey: "mission_alignment",  prefKey: "mission_alignment",   label: "Mission" },
  { adKey: "async_communication",prefKey: "async_communication", label: "Async-friendly" },
  { adKey: "family_friendly",    prefKey: "family_friendly",     label: "Family-friendly" },
];

const LEVELS = ["Intern", "Junior", "Mid", "Senior", "Staff", "Lead", "Principal"];

function fmtSalary(n, cur) {
  if (n == null) return "—";
  const sym = cur === "USD" ? "$" : cur === "EUR" ? "€" : "£";
  return `${sym}${Number(n).toLocaleString()}`;
}

function Stars({ value }) {
  const v = Number(value) || 0;
  const full = Math.floor(v);
  const half = v - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[0,1,2,3,4].map((i) => (
        <Star key={i} size={14} weight={i < full ? "fill" : (i === full && half ? "duotone" : "regular")}
          className={i < full || (i === full && half) ? "text-[var(--brand-primary)]" : "text-[var(--brand-muted)]"}/>
      ))}
      <span className="ml-1 font-mono-d text-xs">{v.toFixed(1)}</span>
    </span>
  );
}

/**
 * Compute personal fit score: weighted average of ad dimension scores against
 * the user's importance weights. Returns null when there's nothing to weight.
 */
function fitScore(adAnalysis, prefs) {
  if (!adAnalysis?.dimensions || !prefs) return null;
  let num = 0;
  let den = 0;
  for (const { adKey, prefKey } of DIMENSIONS) {
    const w = Number(prefs[prefKey]);
    const s = Number(adAnalysis.dimensions[adKey]);
    if (!Number.isFinite(w) || !Number.isFinite(s)) continue;
    num += w * s;
    den += w * 5;
  }
  if (den === 0) return null;
  return Math.round((num / den) * 50) / 10; // → 0.0 - 5.0
}

// ---------- "What matters to you" weights ----------
function WeightsCard({ prefs, onSaved }) {
  const [draft, setDraft] = useState(prefs || {});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(prefs || {}); }, [prefs]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/culture/preferences", draft);
      toast.success("Saved — recomputed all fit scores");
      onSaved?.(draft);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-hard rounded-md p-5 mb-6 bg-white" data-testid="weights-card">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="weights-toggle">
        <p className="label-tiny flex items-center gap-1.5 m-0">
          <Sliders size={12} weight="duotone"/> /what matters to you
        </p>
        <span className="font-mono-d text-xs text-[var(--brand-muted)] flex items-center gap-1">
          {open ? "hide" : "edit weights"} <CaretDown size={12} weight="bold" className={`transition ${open ? "rotate-0" : "-rotate-90"}`}/>
        </span>
      </button>
      {!open && (
        <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-2">
          your importance weights tune the fit score on every job below. higher = matters more.
        </p>
      )}
      {open && (
        <>
          <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-2 mb-4">
            1 = i don&apos;t really care · 5 = critical to me. these weights apply to every saved job.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {DIMENSIONS.map(({ prefKey, label }) => {
              const v = Number(draft[prefKey] ?? 3);
              return (
                <label key={prefKey} className="block text-sm" data-testid={`weight-${prefKey}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{label}</span>
                    <span className="font-mono-d text-xs text-[var(--brand-muted)]">{v}/5</span>
                  </div>
                  <input
                    type="range" min="1" max="5" step="1" value={v}
                    onChange={(e) => setDraft({ ...draft, [prefKey]: Number(e.target.value) })}
                    className="w-full accent-[var(--brand-primary)]"
                    data-testid={`weight-${prefKey}-input`}
                  />
                </label>
              );
            })}
          </div>
          <div className="mt-5 flex items-center gap-2">
            <button onClick={save} disabled={busy} data-testid="weights-save"
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
              <FloppyDisk size={14} weight="bold"/> {busy ? "saving…" : "save & recompute"}
            </button>
            <span className="font-mono-d text-xs text-[var(--brand-muted)]">
              also used by the culture-barometer when analysing job ads.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Add Job Modal — all sections in one form ----------
function AddJobModal({ open, onClose, onSaved, editing }) {
  // editing: existing job object to edit (replaces "create" behaviour)
  const isEdit = !!editing;
  const [form, setForm] = useState(() => makeBlankForm(editing));
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(makeBlankForm(editing)); }, [open, editing]);

  if (!open) return null;

  const submit = async () => {
    if (!form.company.trim() || !form.role.trim()) {
      toast.error("Company and role are required"); return;
    }
    setBusy(true);
    try {
      const skill_ratings = form.skillsText.split(",").map((s) => s.trim()).filter(Boolean).map((s) => {
        const m = s.match(/^([^:]+)(?::(\d+(?:\.\d+)?))?$/);
        const skill = (m?.[1] || s).trim();
        const score = m?.[2] ? Math.max(1, Math.min(5, parseFloat(m[2]))) : 3;
        return { skill, score };
      });
      const payload = {
        company: form.company.trim(),
        role: form.role.trim(),
        skill_ratings,
        years_required: form.years_required ? parseFloat(form.years_required) : null,
        level: form.level || null,
        salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
        salary_max: form.salary_max ? parseFloat(form.salary_max) : null,
        currency: form.currency,
        notes: form.notes,
      };
      if (!isEdit && form.jobText.trim().length >= 30) payload.job_text = form.jobText.trim();

      let saved;
      if (isEdit) {
        saved = await api.patch(`/jobs/${editing.id}`, payload);
        // If they pasted a fresh ad on edit, run analyser separately
        if (form.jobText.trim().length >= 30) {
          saved = await api.post(`/jobs/${editing.id}/analyse-ad`, { job_text: form.jobText.trim() });
        }
        toast.success("Updated");
      } else {
        saved = await api.post("/jobs", payload);
        toast.success(payload.job_text ? "Saved & analysed" : "Saved");
      }
      onSaved?.(saved.data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="add-job-modal">
      <div className="card-hard rounded-md bg-white w-full max-w-3xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="label-tiny flex items-center gap-1.5 m-0">
              <Plus size={12} weight="bold"/> /{isEdit ? "edit job" : "add a job"}
            </p>
            <p className="font-display text-2xl tracking-tight mt-1">
              {isEdit ? "edit job details" : "everything about one job, in one place"}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--brand-bg)] rounded" aria-label="close" data-testid="add-job-close">
            <X size={20}/>
          </button>
        </div>

        {/* Identity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <input data-testid="job-company" placeholder="company *" value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            className="card-hard rounded-md px-3 py-2 bg-white"/>
          <input data-testid="job-role" placeholder="role *" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="card-hard rounded-md px-3 py-2 bg-white"/>
        </div>

        {/* Job ad text (AI) */}
        <div className="mb-5">
          <p className="label-tiny mb-2 flex items-center gap-1.5">
            <Sparkle size={12} weight="duotone"/> /job ad — paste to extract signals via AI {isEdit && <span className="ml-1 text-[10px] font-mono-d text-[var(--brand-muted)]">(re-analyses if changed)</span>}
          </p>
          <textarea data-testid="job-text" value={form.jobText}
            onChange={(e) => setForm({ ...form, jobText: e.target.value })}
            placeholder="paste the full job description here (30+ chars to trigger AI)"
            className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[120px] text-sm"/>
          <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-1">
            optional · we&apos;ll surface red/green flags, dimension scores, and things to weigh up.
          </p>
        </div>

        {/* Skills */}
        <div className="mb-5">
          <p className="label-tiny mb-2 flex items-center gap-1.5">
            <Tag size={12} weight="duotone"/> /skills required
          </p>
          <input data-testid="job-skills" placeholder="Python:5, SQL:3, Stakeholder management:4"
            value={form.skillsText}
            onChange={(e) => setForm({ ...form, skillsText: e.target.value })}
            className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
          <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-1">
            format: <span className="font-mono-d">name:score</span> · score 1-5, defaults to 3.
          </p>
        </div>

        {/* Experience */}
        <div className="mb-5">
          <p className="label-tiny mb-2 flex items-center gap-1.5">
            <GraduationCap size={12} weight="duotone"/> /experience required
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="job-years" type="number" step="0.5" placeholder="years (e.g. 3)"
              value={form.years_required}
              onChange={(e) => setForm({ ...form, years_required: e.target.value })}
              className="card-hard rounded-md px-3 py-2 bg-white"/>
            <select data-testid="job-level" value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
              className="card-hard rounded-md px-3 py-2 bg-white">
              <option value="">-- level --</option>
              {LEVELS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* Compensation */}
        <div className="mb-5">
          <p className="label-tiny mb-2 flex items-center gap-1.5">
            <CurrencyGbp size={12} weight="duotone"/> /compensation
          </p>
          <div className="grid grid-cols-3 gap-3">
            <select data-testid="job-currency" value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="card-hard rounded-md px-3 py-2 bg-white">
              <option>GBP</option><option>USD</option><option>EUR</option>
            </select>
            <input data-testid="job-salary-min" type="number" placeholder="min"
              value={form.salary_min}
              onChange={(e) => setForm({ ...form, salary_min: e.target.value })}
              className="card-hard rounded-md px-3 py-2 bg-white"/>
            <input data-testid="job-salary-max" type="number" placeholder="max"
              value={form.salary_max}
              onChange={(e) => setForm({ ...form, salary_max: e.target.value })}
              className="card-hard rounded-md px-3 py-2 bg-white"/>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <p className="label-tiny mb-2">/notes (just for you)</p>
          <textarea data-testid="job-notes" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="anything else you want to remember about this role"
            className="w-full card-hard rounded-md px-3 py-2 bg-white min-h-[60px] text-sm"/>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={busy} data-testid="job-save"
            className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            <FloppyDisk size={14} weight="bold"/> {busy ? (form.jobText.length >= 30 ? "analysing…" : "saving…") : (isEdit ? "save changes" : "save job")}
          </button>
          <button onClick={onClose}
            className="btn-hard bg-white text-[var(--brand-ink)] rounded-md px-4 py-2 text-sm">
            cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function makeBlankForm(editing) {
  if (!editing) return { company: "", role: "", jobText: "", skillsText: "", years_required: "", level: "", currency: "GBP", salary_min: "", salary_max: "", notes: "" };
  const skillsText = (editing.skill_ratings || []).map((s) => `${s.skill}:${s.score}`).join(", ");
  return {
    company: editing.company || "",
    role: editing.role || "",
    jobText: "",
    skillsText,
    years_required: editing.years_required ?? "",
    level: editing.level || "",
    currency: editing.currency || "GBP",
    salary_min: editing.salary_min ?? "",
    salary_max: editing.salary_max ?? "",
    notes: editing.notes || "",
  };
}

// ---------- Job picker (chips) ----------
function JobPicker({ jobs, selected, onToggle, onDelete, prefs }) {
  if (jobs.length === 0) return null;
  return (
    <div className="card-hard rounded-md p-4 bg-white mb-6" data-testid="job-picker">
      <p className="label-tiny mb-3">/pick up to 3 jobs to compare</p>
      <div className="flex flex-wrap gap-2">
        {jobs.map((j) => {
          const active = selected.includes(j.id);
          const score = fitScore(j.ad_analysis, prefs);
          // A chip is two side-by-side buttons sharing a pill wrapper:
          //  ▸ left button toggles selection (the main click target)
          //  ▸ right button deletes the job (small × with confirm)
          // We can't nest <button> elements, so the wrapper is a non-button div.
          return (
            <div key={j.id} data-testid={`chip-${j.id}`}
              className={`card-hard rounded-full inline-flex items-center transition ${
                active ? "bg-[var(--brand-primary)] text-white"
                       : "bg-white text-[var(--brand-ink)] hover:bg-[var(--brand-bg)]"
              }`}>
              <button onClick={() => onToggle(j.id)}
                data-testid={`pick-${j.id}`}
                disabled={!active && selected.length >= 3}
                className="px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-40 rounded-l-full">
                <span className="font-semibold">{j.company || "?"}</span>
                <span className="opacity-70">·</span>
                <span>{j.role || "?"}</span>
                {score != null && (
                  <span className={`font-mono-d ${active ? "text-white/80" : "text-[var(--brand-muted)]"}`}>
                    · {score.toFixed(1)}
                  </span>
                )}
                {j.ad_analysis && <Sparkle size={11} weight="fill" className={active ? "text-white" : "text-[var(--brand-primary)]"} />}
                {(j.skill_ratings?.length || 0) > 0 && <Tag size={11} weight="fill" className={active ? "text-white" : "text-[var(--bolt-sage)]"} />}
                {(j.years_required != null || j.level) && <GraduationCap size={11} weight="fill" className={active ? "text-white" : "text-[var(--bolt-plum)]"} />}
                {(j.salary_min != null || j.salary_max != null) && <CurrencyGbp size={11} weight="fill" className={active ? "text-white" : "text-[var(--bolt-terracotta)]"} />}
              </button>
              <button onClick={() => onDelete(j.id, j)}
                data-testid={`chip-del-${j.id}`}
                title={`delete ${j.company || "job"}`}
                aria-label={`delete ${j.company || "job"}`}
                className={`px-2 py-1.5 rounded-r-full text-[11px] border-l ${
                  active ? "border-white/30 hover:bg-white/15"
                         : "border-[var(--brand-ink)]/15 hover:bg-[var(--brand-bg)]"
                }`}>
                <X size={11} weight="bold"/>
              </button>
            </div>
          );
        })}
      </div>
      {selected.length > 0 && (
        <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-3">
          {selected.length}/3 selected · tap a chip to deselect · tap × to delete
        </p>
      )}
    </div>
  );
}

// ---------- Section header row ----------
function SectionHeader({ icon: Icon, label, colCount, testid }) {
  return (
    <tr className="bg-[var(--brand-bg)]" data-testid={testid}>
      <td colSpan={colCount + 1} className="p-3 border-t-[1.5px] border-b border-[var(--brand-ink)]/30">
        <p className="label-tiny flex items-center gap-1.5 m-0">
          <Icon size={12} weight="duotone"/> {label}
        </p>
      </td>
    </tr>
  );
}

// ---------- Per-employer community row (lazy-loaded) ----------
function CommunityRow({ jobs }) {
  const [data, setData] = useState({}); // jobId -> bench

  useEffect(() => {
    let live = true;
    (async () => {
      const next = {};
      for (const j of jobs) {
        if (!j.company) continue;
        try {
          const r = await api.get("/jobs/community/by-employer", { params: { company: j.company, role: j.role } });
          next[j.id] = r.data;
        } catch { /* swallow */ }
      }
      if (live) setData(next);
    })();
    return () => { live = false; };
  }, [jobs.map((j) => `${j.company}|${j.role}`).join("~")]);

  return (
    <tr className="border-b border-[var(--brand-ink)]/10" data-testid="community-row">
      <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">how others<br/>rate this employer</td>
      {jobs.map((j) => {
        const b = data[j.id];
        if (!b) return <td key={j.id} className="p-3 font-mono-d text-xs text-[var(--brand-muted)]">loading…</td>;
        if ((b.sample_size || 0) <= 1) {
          return (
            <td key={j.id} className="p-3 font-mono-d text-xs text-[var(--brand-muted)] align-top">
              you&apos;re the only one rating {j.company || "this employer"} on JobVault so far.
            </td>
          );
        }
        const adAvg = b.ad_signals?.avg_stars;
        const compMin = b.compensation?.median_min;
        const compMax = b.compensation?.median_max;
        const years = b.experience?.median_years;
        const level = b.experience?.most_common_level;
        const topSkills = b.skills?.top || [];
        return (
          <td key={j.id} className="p-3 text-xs align-top" data-testid={`community-${j.id}`}>
            <div className="font-mono-d text-[10px] text-[var(--brand-muted)] mb-1">n = {b.sample_size}</div>
            {adAvg != null && (
              <div className="mb-1">avg signals: <b className="font-mono-d">{adAvg}/5</b></div>
            )}
            {(compMin != null || compMax != null) && (
              <div className="mb-1">salary: <b className="font-mono-d">{fmtSalary(compMin, b.compensation?.currency)} — {fmtSalary(compMax, b.compensation?.currency)}</b></div>
            )}
            {(years != null || level) && (
              <div className="mb-1">experience: <b className="font-mono-d">{years ?? "—"}{years ? "y" : ""} {level || ""}</b></div>
            )}
            {topSkills.length > 0 && (
              <div className="mt-1">
                <div className="font-mono-d text-[10px] text-[var(--brand-muted)] mb-1">top skills</div>
                <div className="flex flex-wrap gap-1">
                  {topSkills.slice(0, 5).map((s) => (
                    <span key={s.skill} className="card-hard rounded-full px-1.5 py-0 text-[10px] bg-white">
                      {s.skill} <span className="font-mono-d text-[var(--brand-muted)]">{s.avg_score}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ---------- Comparison table ----------
function ComparisonTable({ jobs, prefs }) {
  if (jobs.length === 0) {
    return (
      <div className="card-hard rounded-md p-10 bg-white text-center" data-testid="compare-empty-selection">
        <ChatTeardropDots size={36} weight="duotone" className="mx-auto mb-3 text-[var(--brand-muted)]"/>
        <p className="font-display text-2xl tracking-tight">pick at least one job above.</p>
        <p className="text-sm text-[var(--brand-muted)] mt-2">
          chips show which sections have data. fit score uses your weights from above.
        </p>
      </div>
    );
  }

  const anyAd      = jobs.some((j) => j.ad_analysis);
  const anySkills  = jobs.some((j) => (j.skill_ratings?.length || 0) > 0);
  const anyExp     = jobs.some((j) => j.years_required != null || j.level);
  const anyComp    = jobs.some((j) => j.salary_min != null || j.salary_max != null);
  const anyAdvice  = jobs.some((j) => j.ad_analysis?.advice);
  const anyFlags   = jobs.some((j) => (j.ad_analysis?.red_flags?.length || 0) > 0 || (j.ad_analysis?.green_flags?.length || 0) > 0);
  const anyNotes   = jobs.some((j) => j.notes);

  return (
    <div className="card-hard rounded-md bg-white overflow-x-auto" data-testid="comparison-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-[1.5px] border-[var(--brand-ink)]">
            <th className="text-left p-3 font-medium label-tiny w-44">/dimension</th>
            {jobs.map((j) => (
              <th key={j.id} className="text-left p-3 align-top min-w-[220px]" data-testid={`col-${j.id}`}>
                <p className="font-display text-base font-semibold tracking-tight">{j.company || "?"}</p>
                <p className="font-mono-d text-xs text-[var(--brand-muted)]">{j.role || "?"}</p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Your fit score */}
          <SectionHeader icon={Heart} label="Your fit score" colCount={jobs.length} testid="section-fit"/>
          <tr className="border-b border-[var(--brand-ink)]/10">
            <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">weighted by your priorities</td>
            {jobs.map((j) => {
              const s = fitScore(j.ad_analysis, prefs);
              return (
                <td key={j.id} className="p-3 align-top" data-testid={`fit-${j.id}`}>
                  {s == null ? (
                    <span className="font-mono-d text-xs text-[var(--brand-muted)]">need ad analysis</span>
                  ) : (
                    <Stars value={s}/>
                  )}
                </td>
              );
            })}
          </tr>

          {/* Ad signals */}
          {anyAd && <SectionHeader icon={Sparkle} label="Job ad signals" colCount={jobs.length} testid="section-signals"/>}
          {anyAd && (
            <>
              <tr className="border-b border-[var(--brand-ink)]/10">
                <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">headline</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-3 text-xs align-top">
                    {j.ad_analysis?.headline || "—"}
                  </td>
                ))}
              </tr>
              {DIMENSIONS.map(({ adKey, label }) => (
                <tr key={adKey} className="border-b border-[var(--brand-ink)]/10">
                  <td className="p-3 font-medium text-xs text-[var(--brand-muted)]">{label}</td>
                  {jobs.map((j) => {
                    const v = j.ad_analysis?.dimensions?.[adKey];
                    return (
                      <td key={j.id} className="p-3 font-mono-d text-sm">
                        {v === null || v === undefined ? "—" : `${v}/5`}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </>
          )}

          {/* Flags */}
          {anyFlags && (
            <>
              <tr className="border-b border-[var(--brand-ink)]/10">
                <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">red flags</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-3 text-xs align-top">
                    {(j.ad_analysis?.red_flags?.length || 0) === 0 ? (
                      <span className="text-[var(--brand-muted)]">—</span>
                    ) : (
                      <ul className="space-y-1">
                        {j.ad_analysis.red_flags.slice(0, 4).map((f, i) => (
                          <li key={`${j.id}-red-${f.phrase || i}`}>
                            <span className="card-hard rounded px-1.5 py-0.5 text-[10px] bg-white font-mono-d">{f.phrase}</span>
                            <div className="text-[var(--brand-muted)] text-[11px] mt-0.5">{f.why}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-[var(--brand-ink)]/10">
                <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">green flags</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-3 text-xs align-top">
                    {(j.ad_analysis?.green_flags?.length || 0) === 0 ? (
                      <span className="text-[var(--brand-muted)]">—</span>
                    ) : (
                      <ul className="space-y-1">
                        {j.ad_analysis.green_flags.slice(0, 4).map((f, i) => (
                          <li key={`${j.id}-green-${f.phrase || i}`}>
                            <span className="card-hard rounded px-1.5 py-0.5 text-[10px] bg-white font-mono-d">{f.phrase}</span>
                            <div className="text-[var(--brand-muted)] text-[11px] mt-0.5">{f.why}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                ))}
              </tr>
            </>
          )}

          {/* Skills */}
          {anySkills && <SectionHeader icon={Tag} label="Skills required" colCount={jobs.length} testid="section-skills"/>}
          {anySkills && (
            <tr className="border-b border-[var(--brand-ink)]/10">
              <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">key skills</td>
              {jobs.map((j) => (
                <td key={j.id} className="p-3 align-top">
                  {(j.skill_ratings?.length || 0) > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {j.skill_ratings.map((s, i) => (
                        <span key={`${j.id}-skill-${s.skill || i}`} className="card-hard rounded-full px-2 py-0.5 text-xs bg-white">
                          {s.skill}{" "}
                          <span className="font-mono-d text-[var(--brand-muted)]">{s.score}/5</span>
                        </span>
                      ))}
                    </div>
                  ) : <span className="font-mono-d text-xs text-[var(--brand-muted)]">—</span>}
                </td>
              ))}
            </tr>
          )}

          {/* Experience */}
          {anyExp && <SectionHeader icon={GraduationCap} label="Experience required" colCount={jobs.length} testid="section-experience"/>}
          {anyExp && (
            <>
              <tr className="border-b border-[var(--brand-ink)]/10">
                <td className="p-3 font-medium text-xs text-[var(--brand-muted)]">years</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-3 font-mono-d">
                    {j.years_required ?? "—"}{j.years_required != null ? "y" : ""}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-[var(--brand-ink)]/10">
                <td className="p-3 font-medium text-xs text-[var(--brand-muted)]">level</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-3 font-mono-d">{j.level || "—"}</td>
                ))}
              </tr>
            </>
          )}

          {/* Compensation */}
          {anyComp && <SectionHeader icon={CurrencyGbp} label="Compensation" colCount={jobs.length} testid="section-compensation"/>}
          {anyComp && (
            <tr className="border-b border-[var(--brand-ink)]/10">
              <td className="p-3 font-medium text-xs text-[var(--brand-muted)]">salary band</td>
              {jobs.map((j) => (
                <td key={j.id} className="p-3 font-mono-d text-sm">
                  {(j.salary_min == null && j.salary_max == null) ? "—" :
                    `${fmtSalary(j.salary_min, j.currency)} — ${fmtSalary(j.salary_max, j.currency)}`}
                </td>
              ))}
            </tr>
          )}

          {/* "Things to weigh up" (renamed from advice — same data) */}
          {anyAdvice && <SectionHeader icon={ChatTeardropDots} label="Things to weigh up" colCount={jobs.length} testid="section-weigh-up"/>}
          {anyAdvice && (
            <tr className="border-b border-[var(--brand-ink)]/10">
              <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">observations</td>
              {jobs.map((j) => (
                <td key={j.id} className="p-3 text-xs text-[var(--brand-muted)] align-top whitespace-pre-line">
                  {j.ad_analysis?.advice || "—"}
                </td>
              ))}
            </tr>
          )}

          {/* Community per-employer */}
          <SectionHeader icon={Buildings} label="Other JobVault users" colCount={jobs.length} testid="section-community"/>
          <CommunityRow jobs={jobs}/>

          {/* Notes */}
          {anyNotes && <SectionHeader icon={ChatTeardropDots} label="Your notes" colCount={jobs.length} testid="section-notes"/>}
          {anyNotes && (
            <tr className="border-b border-[var(--brand-ink)]/10">
              <td className="p-3 font-medium text-xs text-[var(--brand-muted)] align-top">just for you</td>
              {jobs.map((j) => (
                <td key={j.id} className="p-3 text-xs align-top whitespace-pre-line">{j.notes || "—"}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Manage panel ----------
function ManagePanel({ jobs, onDelete, onEdit }) {
  const [open, setOpen] = useState(false);
  if (jobs.length === 0) return null;

  return (
    <div className="mt-8">
      <button onClick={() => setOpen((o) => !o)} data-testid="manage-toggle"
        className="text-xs font-mono-d text-[var(--brand-muted)] flex items-center gap-1 hover:text-[var(--brand-ink)]">
        <CaretDown size={12} weight="bold" className={`transition ${open ? "rotate-0" : "-rotate-90"}`}/>
        /manage saved jobs ({jobs.length})
      </button>
      {open && (
        <div className="card-hard rounded-md bg-white mt-3 overflow-x-auto" data-testid="manage-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-[1.5px] border-[var(--brand-ink)]">
                <th className="text-left p-3 font-medium label-tiny">/company</th>
                <th className="text-left p-3 font-medium label-tiny">/role</th>
                <th className="text-left p-3 font-medium label-tiny">/sections</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-[var(--brand-ink)]/10">
                  <td className="p-3 font-medium">{j.company || "?"}</td>
                  <td className="p-3 text-[var(--brand-muted)]">{j.role || "?"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {j.ad_analysis && <span className="card-hard rounded-full px-2 py-0.5 text-[10px] bg-white flex items-center gap-1"><Sparkle size={10} weight="fill"/> ad</span>}
                      {(j.skill_ratings?.length || 0) > 0 && <span className="card-hard rounded-full px-2 py-0.5 text-[10px] bg-white flex items-center gap-1"><Tag size={10} weight="fill"/> skills</span>}
                      {(j.years_required != null || j.level) && <span className="card-hard rounded-full px-2 py-0.5 text-[10px] bg-white flex items-center gap-1"><GraduationCap size={10} weight="fill"/> exp</span>}
                      {(j.salary_min != null || j.salary_max != null) && <span className="card-hard rounded-full px-2 py-0.5 text-[10px] bg-white flex items-center gap-1"><CurrencyGbp size={10} weight="fill"/> comp</span>}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => onEdit(j)} className="p-1.5 hover:bg-[var(--brand-bg)] rounded mr-1" title="edit" data-testid={`edit-${j.id}`}>
                      <ArrowsClockwise size={14}/>
                    </button>
                    <button onClick={() => onDelete(j.id, j)} className="p-1.5 hover:bg-[var(--brand-bg)] rounded" title="delete" data-testid={`del-${j.id}`}>
                      <Trash size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Page ----------
export default function Compare() {
  const [jobs, setJobs] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [j, p] = await Promise.all([
        api.get("/jobs").catch(() => ({ data: [] })),
        api.get("/culture/preferences").catch(() => ({ data: {} })),
      ]);
      setJobs(j.data || []);
      setPrefs(p.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  // Auto-select first two when nothing is selected & we have data.
  useEffect(() => {
    if (selected.length === 0 && jobs.length > 0) {
      setSelected(jobs.slice(0, Math.min(2, jobs.length)).map((j) => j.id));
    }
    setSelected((cur) => cur.filter((id) => jobs.some((j) => j.id === id)));
  }, [jobs]);

  const toggle = (id) => {
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : (cur.length >= 3 ? cur : [...cur, id])
    );
  };

  const selectedJobs = useMemo(
    () => selected.map((id) => jobs.find((j) => j.id === id)).filter(Boolean),
    [jobs, selected]
  );

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (j) => { setEditing(j); setModalOpen(true); };

  // Single source of truth for deleting a job. Used by both the chip × button
  // in JobPicker and the trash icon inside ManagePanel.
  const delJob = async (id, job) => {
    const label = job?.company ? `the "${job.company}${job.role ? ` · ${job.role}` : ""}" job` : "this job";
    if (!confirm(`Delete ${label} and all its data? This can't be undone.`)) return;
    try {
      await api.delete(`/jobs/${id}`);
      setSelected((cur) => cur.filter((x) => x !== id));
      await loadAll();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  return (
    <AppShell title="Compare Jobs">
      <AIConsentModal/>
      <p className="text-[var(--brand-muted)] mb-6 max-w-2xl">
        one place to weigh up every job you&apos;re considering. paste the ad once, add what you know,
        we score the fit against your priorities — and show how others rate the same employer.
      </p>

      <WeightsCard prefs={prefs} onSaved={(p) => setPrefs(p)}/>

      <button onClick={openAdd} data-testid="add-job-open"
        className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 mb-6">
        <Plus size={16} weight="bold"/> add a job
      </button>

      {loading ? (
        <p className="font-mono-d text-sm text-[var(--brand-muted)]">loading…</p>
      ) : jobs.length === 0 ? (
        <div className="card-hard rounded-md p-10 bg-white text-center" data-testid="compare-empty">
          <ChatTeardropDots size={36} weight="duotone" className="mx-auto mb-3 text-[var(--brand-muted)]"/>
          <p className="font-display text-2xl tracking-tight">no jobs to compare yet.</p>
          <p className="text-sm text-[var(--brand-muted)] mt-2 max-w-md mx-auto">
            click <b>+ add a job</b> above to start. one form captures ad signals, skills, experience, and salary — all linked to the same job.
          </p>
        </div>
      ) : (
        <>
          <JobPicker jobs={jobs} selected={selected} onToggle={toggle} onDelete={delJob} prefs={prefs}/>
          <ComparisonTable jobs={selectedJobs} prefs={prefs}/>
          <ManagePanel jobs={jobs} onDelete={delJob} onEdit={openEdit}/>
        </>
      )}

      <AddJobModal open={modalOpen} onClose={() => setModalOpen(false)}
        onSaved={() => { loadAll(); }} editing={editing}/>
    </AppShell>
  );
}
